import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TONE_BG, TONE_TEXT } from "@/lib/widget-status";

export type KpiTone = "default" | "positive" | "negative" | "warning";

interface KpiCardProps {
  title: string;
  /** Pre-formatted main value, e.g. "1,2 MNOK". */
  value: string;
  /** Secondary line, e.g. "78 % av mål" or "Est. BF 240 000 kr". */
  subtitle?: string;
  /** Progress toward target (0–100+). Renders a bar when provided. */
  pct?: number | null;
  tone?: KpiTone;
  isLoading?: boolean;
}

const toneText: Record<KpiTone, string> = {
  default: "text-foreground",
  positive: TONE_TEXT.reached,
  negative: TONE_TEXT.miss,
  warning: TONE_TEXT.near,
};

const toneBar: Record<KpiTone, string> = {
  default: "bg-primary",
  positive: TONE_BG.reached,
  negative: TONE_BG.miss,
  warning: TONE_BG.near,
};

/**
 * Generic KPI card for the sales dashboard (F1). Pure presentation — the
 * container computes values via dashboard-bf-calc and passes formatted strings.
 */
export function KpiCard({ title, value, subtitle, pct, tone = "default", isLoading }: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <div className="h-8 w-24 animate-pulse rounded bg-muted" />
        ) : (
          <div className={cn("text-2xl font-semibold tabular-nums", toneText[tone])}>{value}</div>
        )}
        {subtitle && !isLoading && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
        {pct != null && !isLoading && (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn("h-full rounded-full transition-all", toneBar[tone])}
              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
